/**
 * Custom carousel: scroll-snap slider — native scrolling, JS wires the
 * controls + autoplay. Loop (default): a head clone of the last slide
 * plus a viewport-wide tail run of the first slides, so steps walk
 * through copies; settling on a clone snaps silently (no animation) to
 * its real twin — clones are aria-hidden + inert. Non-loop
 * (data-loop="false", reviews): no clone rail, native ends are hard.
 *
 * Resize: a width change moves the slides under a scrollLeft that is still
 * the old pixel value, so the track lands between snap points and shows a
 * slide half in view — it reads as half-scrolled. onResize() debounces,
 * resizeNow() re-derives the geometry and re-parks on the slide that was
 * in view.
 */
// Debounced like reviews-board.js: a drag-resize fires continuously, and each
// run measures layout (getBoundingClientRect) and re-clones when n-up changes.
const RESIZE_DEBOUNCE_MS = 150;

class CustomCarousel extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('.ccarousel__track');
    this.slides = Array.from(this.querySelectorAll('.ccarousel__slide')); // real slides only
    this.dots = Array.from(this.querySelectorAll('.ccarousel__dot'));
    this.rail = []; // head clone + real slides + tail clone (physical scroll order)
    this.loop = true; // set from data-loop in connectedCallback (before buildLoop)
    this.autoplayInterval = null;
    this.scrollRaf = null;
    this.settleTimer = null;
    this.jumpLock = false;
    this.resizeTimer = null;
    this.trackWidth = 0; // track width the rail was last measured at
    this.perView = 0; // slides-per-view the tail clones were cut for
    this.onResize = this.onResize.bind(this);
  }

  connectedCallback() {
    this.loop = this.dataset.loop !== 'false';
    this.buildLoop();
    this.bindEvents();
    this.startAutoplay();
    // Baseline resizeNow() compares against: only a width change moves slides
    // under the scroll position (a mobile URL-bar resize is height-only).
    this.trackWidth = this.track ? this.track.clientWidth : 0;
  }

  disconnectedCallback() {
    this.stopAutoplay();
    if (this.settleTimer) clearTimeout(this.settleTimer);
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
    window.removeEventListener('resize', this.onResize);
  }

  buildLoop() {
    if (!this.track || this.slides.length < 2 || this.rail.length) return;

    // Non-loop (data-loop="false"): no clones — the rail IS the real
    // slides, so the track's native scroll bounds are the hard ends.
    if (!this.loop) {
      this.rail = this.slides.slice();
      return;
    }

    const first = this.slides[0];
    const last = this.slides[this.slides.length - 1];

    // Slides side by side in one viewport — the tail needs a full viewport
    // of clones for the track to scroll far enough (see the header note).
    // Degenerate measurement (no layout yet) falls back to the one-clone
    // behaviour rather than spraying clones.
    const perView = this.slidesPerView() || 1;

    const head = last.cloneNode(true);
    const tails = this.slides.slice(0, perView).map((slide) => slide.cloneNode(true));

    [head, ...tails].forEach((clone) => {
      clone.dataset.clone = 'true';
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      clone.removeAttribute('id');
      // don't duplicate the theme editor's block anchors
      Array.from(clone.attributes)
        .filter((attr) => /^data-(shopify|block)/.test(attr.name))
        .forEach((attr) => clone.removeAttribute(attr.name));
    });

    this.track.insertBefore(head, first);
    tails.forEach((clone) => this.track.appendChild(clone));
    this.rail = [head, ...this.slides, ...tails];
    this.perView = perView; // what the tail run was cut for — re-checked on resize

    // park on the real first slide before paint — the head clone sits at 0
    const behavior = this.track.style.scrollBehavior;
    this.track.style.scrollBehavior = 'auto';
    this.track.scrollLeft = this.rail[1].offsetLeft;
    this.track.style.scrollBehavior = behavior;
  }

  // Slides side by side across the track at the current width — how wide the
  // tail clone run has to be (buildLoop). Null while there is nothing to
  // measure (no track, no slides, or no layout yet): a caller that has to
  // decide something from this must not treat "not laid out" as a real n-up.
  slidesPerView() {
    if (!this.track || !this.slides.length) return null;
    const slideWidth = this.slides[0].getBoundingClientRect().width;
    if (!slideWidth) return null;
    return Math.max(1, Math.min(this.slides.length, Math.round(this.track.clientWidth / slideWidth)));
  }

  // Drop the clones and cut them again at the current slides-per-view. The
  // tail run is exactly one viewport wide (buildLoop), so a breakpoint that
  // flips n-up leaves it the wrong length: the far end of the rail then sits
  // past maxScroll, clamps, and shows a half slide no matter where we scroll.
  rebuildLoop() {
    if (!this.track || !this.loop) return;
    this.track.querySelectorAll('[data-clone]').forEach((clone) => clone.remove());
    this.rail = [];
    this.buildLoop();
  }

  physicalIndex() {
    if (!this.rail.length || !this.track) return 0;
    const width = this.rail[0].offsetWidth || 1;
    return Math.max(0, Math.min(this.rail.length - 1, Math.round(this.track.scrollLeft / width)));
  }

  // Logical = position among the real slides (dots, autoplay, forced states).
  // Rail = [head, s0…s(N-1), tail copies of s0…s(T-1)], so every tail clone
  // maps to its own logical slide, not just the last one. Non-loop rails are
  // the plain slide list — physical IS logical.
  get currentIndex() {
    if (this.slides.length === 0) return 0;
    const physical = this.physicalIndex();
    if (!this.loop) return Math.max(0, Math.min(this.slides.length - 1, physical));
    if (physical === 0) return this.slides.length - 1; // head clone ≡ last slide
    if (physical > this.slides.length) return physical - this.slides.length - 1; // tail k ≡ slide k
    return Math.min(this.slides.length - 1, physical - 1);
  }

  scrollToPhysical(index, behavior = 'smooth') {
    const target = this.rail[Math.max(0, Math.min(this.rail.length - 1, index))];
    if (!target) return;
    this.track.scrollTo({ left: target.offsetLeft, behavior });
  }

  // Land a clone on its real twin without animating: same offset, so
  // nothing visibly moves. Guards the scroll handler while it happens.
  jumpToReal(index) {
    this.jumpInstant(this.rail[index].offsetLeft);
    this.updateDots();
  }

  // One instant scroll: the style swap is what actually disables the smooth
  // scroll (behaviour: 'auto' would still honour the track's CSS
  // scroll-behavior), and the scroll handler is held off for the two frames
  // the jump's scroll event can take to land.
  jumpInstant(left) {
    this.jumpLock = true;
    const behavior = this.track.style.scrollBehavior;
    this.track.style.scrollBehavior = 'auto';
    this.track.scrollLeft = left;
    this.track.style.scrollBehavior = behavior;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      this.jumpLock = false;
    }));
  }

  onSettle() {
    // Clone-landing snaps only exist on a looping rail.
    if (this.jumpLock || !this.rail.length || !this.loop) return;
    const physical = this.physicalIndex();
    if (physical === 0) this.jumpToReal(this.slides.length); // head clone ≡ last real (rail slot N)
    else if (physical > this.slides.length) this.jumpToReal(physical - this.slides.length); // tail k ≡ real rail slot k+1
  }

  bindEvents() {
    if (!this.track) return;

    this.track.addEventListener('scroll', () => {
      if (this.jumpLock) return;
      if (this.scrollRaf) return;
      this.scrollRaf = requestAnimationFrame(() => {
        this.scrollRaf = null;
        this.updateDots();
      });
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.onSettle(), 150);
    });

    window.addEventListener('resize', this.onResize);

    this.addEventListener('mouseenter', () => this.pauseAutoplay());
    this.addEventListener('mouseleave', () => this.startAutoplay());
    this.addEventListener('focusin', () => this.pauseAutoplay());
    this.addEventListener('focusout', () => this.startAutoplay());

    this.querySelector('.ccarousel__arrow--prev')?.addEventListener('click', () => {
      this.prev();
      this.restartAutoplay();
    });
    this.querySelector('.ccarousel__arrow--next')?.addEventListener('click', () => {
      this.next();
      this.restartAutoplay();
    });

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.goTo(index);
        this.restartAutoplay();
      });
    });
  }

  onResize() {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => this.resizeNow(), RESIZE_DEBOUNCE_MS);
  }

  // Width changed: scrollLeft still holds the old pixel offset while the
  // slides have resized, so the track sits between snap points and shows a
  // slide half in view — it reads as half-scrolled. Re-derive whatever was
  // measured from the width (the tail clone run), then force the track back
  // onto the slide that was in view.
  resizeNow() {
    if (!this.track || !this.rail.length) return;
    const width = this.track.clientWidth;
    // Height-only (URL bar) or a hidden track — neither moves slides, and a
    // hidden track measures 0, which must not be mistaken for a new width.
    if (!width || width === this.trackWidth) return;
    this.trackWidth = width;
    // Resolve "current" before anything rebuilds: rebuildLoop parks on the
    // first slide, which would overwrite the scroll position the pixel
    // fallback reads (the js-active mark survives a rebuild either way).
    const logical = this.currentLogical();
    const perView = this.slidesPerView();
    if (this.loop && perView !== null && perView !== this.perView) this.rebuildLoop();
    this.recenter(logical);
  }

  // The slide in view, as an index into this.slides. The js-active mark
  // updateDots() leaves is the only record of "current" that survives a width
  // change — physicalIndex() reads scrollLeft against the *new* slide width,
  // which says nothing about where the old position came from. The pixel
  // reading is only the fallback for a first paint that hasn't scrolled yet,
  // so nothing has marked a slide.
  currentLogical() {
    const marked = this.slides.findIndex((slide) => slide.classList.contains('js-active'));
    if (marked >= 0) return marked;
    if (!this.slides.length) return 0;
    return Math.max(0, Math.min(this.slides.length - 1, this.currentIndex));
  }

  // Force the track onto the slide that was in view, with no animation.
  recenter(logical = this.currentLogical()) {
    if (!this.rail.length || !this.slides.length) return;
    const target = this.rail[this.loop ? logical + 1 : logical]; // loop rails sit one over (head clone first)
    if (!target) return;
    // Exact, unlike scrollToPhysical(): offsetLeft is measured against the
    // host, and a grid-laid track sits columns into it — only the difference
    // scrolls (next() measures the same way). There is no snap correction to
    // fall back on here, because landing off the snap point is the bug.
    this.jumpInstant(target.offsetLeft - this.track.offsetLeft);
    this.updateDots(logical);
  }

  // Dots address real slides; the physical rail sits one over (head clone first).
  goTo(index) {
    if (this.slides.length === 0) return;
    if (!this.loop) {
      const clamped = Math.max(0, Math.min(this.slides.length - 1, index));
      this.scrollToPhysical(clamped);
      this.updateDots(clamped);
      return;
    }
    const wrapped = ((index % this.slides.length) + this.slides.length) % this.slides.length;
    this.scrollToPhysical(wrapped + 1);
    this.updateDots(wrapped);
  }

  // One physical step forward — from the last real slide this lands on the
  // first tail clone (a copy of the first slide), which onSettle() snaps
  // back silently. The tail run is a viewport wide (buildLoop), so that
  // target is always within maxScroll on a laid-out track.
  next() {
    if (!this.rail.length) return;
    const physical = this.physicalIndex();
    // Non-loop: the last slide (the reviews "see all" card) is a hard
    // end — step forward only while a real slide remains, and park at
    // the true maxScroll when the next step would overshoot it
    // (multi-view: the last slide can sit mid-runway). Overshooting
    // means the final view is already showing, so parking clamps to it
    // and further next() calls are no-ops.
    if (!this.loop) {
      const target = physical + 1;
      if (target > this.rail.length - 1) return;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const targetLeft = this.rail[target].offsetLeft - this.track.offsetLeft;
      if (targetLeft > maxScroll + 1) {
        this.track.scrollTo({ left: maxScroll, behavior: 'smooth' });
        return;
      }
      this.scrollToPhysical(target);
      return;
    }
    // `2` = the slide after s0: at the tail end the rail has run out and
    // the only logical reading is "we are showing s0's view" (on 1-up that
    // is the reachable last slot; on multi-view it never fires).
    const target = physical < this.rail.length - 1 ? physical + 1 : 2;
    // Safety net: a target past maxScroll (unlaid-out track) jumps to the
    // first real slide — a plain scrollTo would clamp and jam on the last
    // reachable one; clones are copies, so the jump is picture-identical.
    // Track-relative: offsetLeft is measured against the host, but a
    // grid-laid track sits columns into it — only the difference scrolls;
    // +1 covers scrollWidth truncating to int vs offsetLeft fractions.
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const targetLeft = this.rail[target].offsetLeft - this.track.offsetLeft;
    if (targetLeft > maxScroll + 1) {
      this.jumpToReal(1);
      return;
    }
    this.scrollToPhysical(target);
  }

  // One physical step back — from the first real slide this lands on the
  // head clone (a copy of the last), which onSettle() snaps back silently.
  prev() {
    if (!this.rail.length) return;
    // Non-loop: clamp at the first slide, no head-clone wrap.
    if (!this.loop) {
      const physical = this.physicalIndex();
      if (physical <= 0) return;
      this.scrollToPhysical(physical - 1);
      return;
    }
    // Already parked on the head clone: snap to its real twin first (same
    // picture, no motion) so the step back scrolls one slide left instead
    // of sweeping the whole runway forward.
    if (this.physicalIndex() === 0) this.jumpToReal(this.slides.length);
    const physical = this.physicalIndex();
    const target = physical > 0 ? physical - 1 : this.slides.length - 1;
    this.scrollToPhysical(target);
  }

  updateDots(forced) {
    const active = forced ?? this.currentIndex;
    this.dots.forEach((dot, index) => {
      dot.dataset.active = String(index === active);
    });
    this.slides.forEach((slide, index) => {
      slide.classList.toggle('js-active', index === active);
    });
  }

  startAutoplay() {
    const enabled = this.dataset.autoplay === 'true';
    const speed = parseInt(this.dataset.speed || '5', 10) * 1000;
    if (!enabled || this.autoplayInterval || this.slides.length < 2) return;
    this.autoplayInterval = setInterval(() => {
      // Only continue if still in view (prevent background churn)
      if (document.hidden) return;
      const rect = this.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;
      this.next();
    }, speed);
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }

  pauseAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }
}

if (!customElements.get('custom-carousel')) {
  customElements.define('custom-carousel', CustomCarousel);
}
